import {
    Flex,
    Icon,
    IconButton,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Popover,
    PopoverArrow,
    PopoverContent,
    PopoverTrigger,
    Select,
    Tag,
    TagLabel,
    Td,
    Text,
    ThemeTypings,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { BiDuplicate, BiX } from 'react-icons/bi';

import { useAssetPrice } from '../api/pricing';
import { PortfolioSelector } from '../widgets/PortfolioSelector';
import { MoneyCell } from '../widgets/Table/MoneyCell';
import { PriceSelector } from './PriceSelector';
import { SecuritySelector } from './SecuritySelector';
import { ManualTrade, PriceType } from './TradeBlotter';
import { useTradeBlotterContext } from './TradeBlotterContext';
import { tradeCalculator } from './tradeCalculator';

export const TradeLineItem = ({
    index,
    viewPortfolio,
    viewAsset,
}: {
    index: number;
    viewPortfolio: (portfolio: string) => void;
    viewAsset: (asset: string) => void;
}) => {
    const { trades, addTrade, updateTrade, removeTrade } = useTradeBlotterContext();
    const trade = trades[index];

    const onChange = useCallback((diff: ManualTrade) => updateTrade(diff, index), [index, updateTrade]);

    const { data: quote } = useAssetPrice(trade.asset?.sId);

    useEffect(() => {
        if (quote && !isEqual(trade?.quote, quote)) {
            onChange({ quote });
        }
    }, [onChange, quote, trade]);

    useEffect(() => {
        if (trade.budget) {
            const quantity = tradeCalculator.getTradeQuantity(trade);
            if (quantity !== trade.quantity) {
                onChange({ quantity });
            }
        }
    }, [onChange, trade]);

    return (
        <Tr>
            <Td borderColor="gray.600" px={2} py={1} verticalAlign="top" minWidth="200px">
                <PortfolioSelector
                    isInvalid={!trade.portfolio?.pId}
                    identifier={trade.portfolio?.portfolioGuid || trade.portfolio?.aC || trade.portfolio?.pId}
                    onSelect={({ base }) => {
                        onChange({ portfolio: base });
                    }}
                />
                <Text
                    color="teal.200"
                    fontSize="xs"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    width="fit-content"
                    maxWidth="240px"
                    onClick={() => trade.portfolio && viewPortfolio(trade.portfolio?.pId)}
                    _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                    {trade.portfolio?.accountAlias}
                </Text>
            </Td>
            <Td borderColor="gray.600" px={2} py={1} minWidth="100px" verticalAlign="top">
                <Side trade={trade} onChange={onChange} />
            </Td>
            <Td borderColor="gray.600" px={2} py={1} minWidth="280px" maxWidth="500px" verticalAlign="top">
                <SecuritySelector
                    isInvalid={!trade.asset?.sId}
                    identifier={trade.asset?.symbol || trade.asset?.cusip || trade.asset?.sId}
                    onSelect={(asset) => {
                        onChange({ asset });
                    }}
                />
                <Text
                    color="teal.200"
                    fontSize="xs"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxWidth="240px"
                    onClick={() => trade.asset && viewAsset(trade.asset?.sId)}
                    _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                    {trade.asset?.description}
                </Text>
            </Td>
            <Td borderColor="gray.600" px={2} py={1} minWidth="280px" maxWidth="300px" verticalAlign="top">
                <Price trade={trade} onChange={onChange} />
                <DirtyPrice trade={trade} />
            </Td>
            <Td borderColor="gray.600" px={2} py={1} verticalAlign="top">
                <OrderType trade={trade} onChange={onChange} />
            </Td>
            <Td borderColor="gray.600" px={2} py={1} minWidth="100px" verticalAlign="top">
                <Quantity trade={trade} onChange={onChange} />
                <TradeValue trade={trade} />
                <TradeBudget trade={trade} onChange={onChange} />
            </Td>
            <Td borderColor="gray.600" px={2} py={1}>
                <Flex flexDirection="column" gap={1} ml="auto" width="fit-content">
                    <IconButton
                        size="xs"
                        icon={<Icon as={BiDuplicate} />}
                        aria-label="duplicate move"
                        colorScheme="green"
                        onClick={() => addTrade(trade, index)}
                        variant="outline"
                    />
                    <IconButton
                        size="xs"
                        icon={<Icon as={BiX} />}
                        aria-label="remove trade"
                        colorScheme="red"
                        onClick={() => removeTrade(index)}
                        variant="outline"
                    />
                </Flex>
            </Td>
        </Tr>
    );
};

const Side = ({ trade, onChange }: { trade: ManualTrade; onChange: (diff: ManualTrade) => void }) => {
    return (
        <Select
            size="sm"
            value={trade.side}
            onChange={(ev) => {
                const value = ev.target.value;
                onChange({ side: value, price: { type: tradeCalculator.getPriceTypeFromTradeSide(value) } });
            }}
            isInvalid={!trade.side}
        >
            <option></option>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
        </Select>
    );
};

const Price = ({ trade, onChange }: { trade: ManualTrade; onChange: (diff: ManualTrade) => void }) => {
    return (
        <Flex alignItems="center" gap={2}>
            <PriceSelector
                trade={trade}
                clearManualPrice={() =>
                    onChange({ price: { type: tradeCalculator.getPriceTypeFromTradeSide(trade.side) } })
                }
                onChange={(price) => onChange({ price })}
            />
            <Flex alignItems="center" gap={1} minWidth="140px">
                <PriceTypeTag priceType={trade.price?.type} />
                {tradeCalculator.getPriceDeviation(trade)}
            </Flex>
        </Flex>
    );
};

const DirtyPrice = ({ trade }: { trade: ManualTrade }) => {
    if (trade.asset?.assetClass !== 'Bond') {
        return null;
    }

    return (
        <>
            <Flex gap="2px" alignItems="center">
                <Text fontSize="xs">Accrued Interest:</Text>
                <MoneyCell
                    size="xs"
                    textAlign="left"
                    value={tradeCalculator.getAccruedInterest(trade)}
                    fractionDigits={4}
                />
            </Flex>
            <Flex gap="2px" alignItems="center">
                <Text fontSize="xs">Dirty Price:</Text>
                <MoneyCell
                    size="xs"
                    textAlign="left"
                    value={tradeCalculator.getAdjustedDirtyPrice(trade)}
                    fractionDigits={4}
                />
            </Flex>
        </>
    );
};

const OrderType = ({ trade, onChange }: { trade: ManualTrade; onChange: (diff: ManualTrade) => void }) => {
    return (
        <Select
            value={trade.orderType}
            size="sm"
            onChange={(ev) => {
                const value = ev.target.value;
                onChange(
                    value === 'MKT'
                        ? { orderType: 'MKT', price: { type: 'MID' } }
                        : { orderType: 'LMT', price: { type: tradeCalculator.getPriceTypeFromTradeSide(trade.side) } },
                );
            }}
        >
            <option value="LMT">LMT</option>
            <option value="MKT">MKT</option>
        </Select>
    );
};

const Quantity = ({ trade, onChange }: { trade: ManualTrade; onChange: (diff: ManualTrade) => void }) => {
    return (
        <NumberInput
            size="sm"
            value={trade.quantity !== undefined ? Number(trade.quantity) : 0}
            onChange={(_, quantity) => {
                onChange({ quantity: quantity || 0, budget: trade.quantity !== quantity ? undefined : trade.budget });
            }}
            precision={0}
            isInvalid={!trade.quantity}
        >
            <NumberInputField />
            <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
            </NumberInputStepper>
        </NumberInput>
    );
};

const TradeBudget = ({ trade, onChange }: { trade: ManualTrade; onChange: (diff: ManualTrade) => void }) => {
    const { onOpen, onClose, isOpen } = useDisclosure();
    const initialFocusRef = useRef<HTMLInputElement | null>(null);

    if (!trade.price || !trade.asset) {
        return null;
    }

    return (
        <Flex gap={1} alignItems="center">
            <Popover
                isLazy
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={() => onClose()}
                placement="bottom"
                closeOnBlur={true}
                initialFocusRef={initialFocusRef}
            >
                <PopoverTrigger>
                    <Text color="teal.200" fontSize="xs" _hover={{ textDecoration: 'underline', cursor: 'pointer' }}>
                        Trade Budget:
                    </Text>
                </PopoverTrigger>
                <PopoverContent p={4} boxShadow="dark-lg">
                    <PopoverArrow />
                    <Flex alignItems="center" gap={2}>
                        <Text>Set budget for trade</Text>
                        <Flex gap={4}>
                            <NumberInput
                                size="sm"
                                value={trade.budget}
                                onChange={(_, budget) => onChange({ budget: budget || 0 })}
                            >
                                <NumberInputField ref={initialFocusRef} />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                        </Flex>
                    </Flex>
                </PopoverContent>
            </Popover>
            <MoneyCell size="xs" textAlign="left" value={trade.budget} />
        </Flex>
    );
};

const TradeValue = ({ trade }: { trade: ManualTrade }) => {
    return (
        <Flex gap={1}>
            <Text fontSize="xs">Trade Value:</Text>
            <MoneyCell size="xs" textAlign="left" value={tradeCalculator.getTradeValue(trade)} />
        </Flex>
    );
};

const colorScheme: { [k in PriceType]: ThemeTypings['colorSchemes'] } = {
    ASK: 'red',
    BID: 'blue',
    MID: 'gray',
    MANUAL: 'orange',
};

const PriceTypeTag = ({ priceType }: { priceType?: PriceType }) => {
    if (!priceType) {
        return null;
    }
    return (
        <Tag size="sm" variant="subtle" colorScheme={colorScheme[priceType]}>
            <TagLabel>{priceType}</TagLabel>
        </Tag>
    );
};
